import { type FormEvent } from "react";
import { FileUpload } from "../../shared/FileUpload";
import {
  REPORT_TYPE_HINTS,
  type BafaReportType,
  type Form1State,
} from "./state";

interface Props {
  form: Form1State;
  loading: boolean;
  onChange: (patch: Partial<Form1State>) => void;
  onSubmit: () => void;
  onResetChat: () => void;
}

export function Step1Inputs({ form, loading, onChange, onSubmit, onResetChat }: Props) {
  const hint = form.reportType ? REPORT_TYPE_HINTS[form.reportType as BafaReportType] : "";

  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!form.reportType) return;
    onSubmit();
  }

  function appendToField(field: keyof Form1State, md: string, fileName: string) {
    const current = form[field] as string;
    const prefix = current.trim() ? "\n\n---\n\n" : "";
    const header = `<!-- Aus: ${fileName} -->\n\n`;
    onChange({ [field]: current + prefix + header + md } as Partial<Form1State>);
  }

  return (
    <section className="wizard-section" data-step="1">
      <h2 className="form-title">Schritt 1: Angaben und Website</h2>
      <form className="report-form" onSubmit={submit}>
        <div className="form-row">
          <label htmlFor="report-type">Berichtstyp</label>
          <select
            id="report-type"
            required
            value={form.reportType}
            onChange={(e) => onChange({ reportType: e.target.value as BafaReportType | "" })}
            aria-describedby="report-type-hint"
          >
            <option value="">— Bitte wählen —</option>
            <option value="lead_easy">Lead Easy (Dr. Florian Marthaler – LinkedIn/Sales B2B)</option>
            <option value="sorhage">Sorhage Solutions (Niklas Sorhage – Prozess-/Kommunikationsberatung)</option>
            <option value="generic">Dynamisch (aus Beratervertrag – Kapitelkonzeption)</option>
          </select>
          <span id="report-type-hint" className="field-hint" aria-live="polite">{hint}</span>
        </div>

        <div className="form-row">
          <label htmlFor="website-url">
            Website-URL <span className="field-hint">(optional)</span>
          </label>
          <input
            id="website-url"
            type="url"
            placeholder="https://…"
            value={form.websiteUrl}
            onChange={(e) => onChange({ websiteUrl: e.target.value })}
          />
        </div>

        <div className="form-row">
          <label htmlFor="mitarbeitende">Mitarbeiterzahl</label>
          <select
            id="mitarbeitende"
            value={form.mitarbeitende}
            onChange={(e) => onChange({ mitarbeitende: e.target.value })}
          >
            <option value="">— Bitte wählen —</option>
            <option value="1-10">1–10</option>
            <option value="11-50">11–50</option>
            <option value="51-250">51–250</option>
            <option value="250+">250+</option>
            <option value="nicht_angegeben">Nicht angegeben</option>
          </select>
        </div>

        <div className="form-row form-row-full">
          <label htmlFor="kernproblem">Kernproblem / Anlass der Beratung</label>
          <textarea
            id="kernproblem"
            rows={3}
            placeholder="Warum wird beraten?"
            value={form.kernproblem}
            onChange={(e) => onChange({ kernproblem: e.target.value })}
          />
          <FileUpload
            inputId="kernproblem-upload"
            hint="oder Datei hochladen (PDF, DOCX, TXT, Audio, Bild) — Konvertierung im Hintergrund"
            onConverted={(md, name) => appendToField("kernproblem", md, name)}
          />
        </div>

        <div className="form-inline-group">
          <div className="form-row form-row-inline">
            <label htmlFor="zeitraum-start">Beratungszeitraum Start</label>
            <input
              id="zeitraum-start"
              type="date"
              value={form.zeitraumStart}
              onChange={(e) => onChange({ zeitraumStart: e.target.value })}
            />
          </div>
          <div className="form-row form-row-inline">
            <label htmlFor="zeitraum-end">Beratungszeitraum Ende</label>
            <input
              id="zeitraum-end"
              type="date"
              value={form.zeitraumEnd}
              onChange={(e) => onChange({ zeitraumEnd: e.target.value })}
            />
          </div>
        </div>

        <div className="form-row form-row-full">
          <label htmlFor="transkripte">Transkripte / Notizen / PDFs</label>
          <textarea
            id="transkripte"
            rows={2}
            placeholder="Falls vorhanden: Aus Gesprächen oder Verträgen (hier einfügen oder beschreiben)"
            value={form.transkripte}
            onChange={(e) => onChange({ transkripte: e.target.value })}
          />
          <FileUpload
            inputId="transkripte-upload"
            hint="oder Datei hochladen (PDF, DOCX, TXT, Audio, Bild) — Konvertierung im Hintergrund"
            onConverted={(md, name) => appendToField("transkripte", md, name)}
          />
        </div>

        <div className="form-row form-row-full">
          <label htmlFor="muendliche-absprachen">Mündliche Absprachen</label>
          <textarea
            id="muendliche-absprachen"
            rows={2}
            placeholder="Datum, Inhalt, Beteiligte"
            value={form.muendlicheAbsprachen}
            onChange={(e) => onChange({ muendlicheAbsprachen: e.target.value })}
          />
          <FileUpload
            inputId="muendliche-absprachen-upload"
            hint="oder Datei hochladen (PDF, DOCX, TXT, Audio, Bild) — Konvertierung im Hintergrund"
            onConverted={(md, name) => appendToField("muendlicheAbsprachen", md, name)}
          />
        </div>

        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={loading || !form.reportType}>
            {loading ? "Wird geladen…" : "Website scrapen und Unternehmensdaten laden"}
          </button>
          <button type="button" className="btn btn-secondary" onClick={onResetChat}>
            Chat zurücksetzen
          </button>
        </div>
      </form>
    </section>
  );
}
